/**
 * The country's route tests in one fact for the header (user, 19/10/2026:
 * "set the route tests info in the header"): how today's test drops stand
 * (planned / sent / done, the same counts as the board), when the first one
 * goes, whether it goes before the send window when its campaign group uses
 * the Route tests strategy (the editor's own shared check), and which test
 * a click opens. Null when the country has none. Before the day plan
 * answers it says so — never "None today" (23/10/2026).
 * For Media Devoted | Created By Ciro Autiero
 */
import { ROUTE_TEST_KIND, ROUTE_TEST_STRATEGY, routeTestClockOnCountry, routeTestDayProblem, type DayPlanPreview } from '@trafficpulse/shared';

import type { DropGenContainerSummary, DropGenPlanDetail } from '../../../drop-generator.types';
import { planDay } from '../../../utils/clock/plan-day';
import { deriveContainerLive } from '../../live/live-status';
import type { FactStatus, FactTone } from '../facts/generation-fact';

type PlanLike = Pick<DropGenPlanDetail, 'containers' | 'masters' | 'recipientTimezone' | 'windowStartMin'>;

export interface RouteTestFact {
  /** "20 planned", "3 of 20 sent", "20 sent", "Off", "None today", "—" (no plan yet). */
  value: string;
  /** Today's progress: not generated, waiting, sending, done, off. */
  progress: FactStatus;
  /** Route-test containers of the country (on or off). */
  count: number;
  /** The first live test drop today, a country minute; null = none. */
  firstMin: number | null;
  /** The test a click opens: the first one switched on, else the first. */
  openId: string;
  off: boolean;
  /** 23/10/2026 — the day plan has not answered: nothing is known about today's drops yet. */
  awaitingPlan: boolean;
  /** Route tests strategy only: does its first drop go before the send window? */
  window: { ok: boolean; text: string } | null;
}

const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);

export function routeTestFact(plan: PlanLike, preview: DayPlanPreview | undefined, runDate?: string | null): RouteTestFact | null {
  const tests = plan.containers.filter((c) => c.kind === ROUTE_TEST_KIND);
  if (tests.length === 0) return null;
  const on = tests.filter((c) => c.enabled);
  const rows = (preview?.containers ?? []).filter((r) => on.some((c) => c.id === r.containerId));
  const minutes = rows.flatMap((r) => r.drops.filter((d) => !d.runtime?.tombstoned).map((d) => d.minute));
  const open = on[0] ?? tests[0];
  return {
    ...standing(on.length, preview ? rows.map((r) => deriveContainerLive(r)) : null),
    count: tests.length,
    firstMin: minutes.length > 0 ? Math.min(...minutes) : null,
    openId: open.id,
    off: on.length === 0,
    awaitingPlan: !preview && on.length > 0,
    window: on[0] ? windowCheck(plan, on[0], runDate) : null,
  };
}

/** The test drops' count and today's progress — the same counts as the board; null facts = no day plan yet. */
function standing(onCount: number, facts: ReturnType<typeof deriveContainerLive>[] | null): Pick<RouteTestFact, 'value' | 'progress'> {
  const of = (value: string, text: string, tone: FactTone) => ({ value, progress: { text, tone } });
  if (onCount === 0) return of('Off', 'Switched off', 'muted');
  if (!facts) return of('—', 'Today’s plan not loaded yet', 'muted'); // the day plan has not answered (or failed)
  const planned = sum(facts.map((f) => f.planned));
  const sent = sum(facts.map((f) => f.sent));
  if (planned === 0) return of('None today', 'Nothing to test today', 'muted');
  if (facts.every((f) => f.state === 'not-generated')) return of(`${planned} planned`, 'Not generated yet', 'muted');
  if (sent >= planned) return of(`${sent} sent`, 'Done for today', 'success');
  return of(`${sent} of ${planned} sent`, sent > 0 ? 'Sending' : 'Waiting for the first drop', sent > 0 ? 'live' : 'muted');
}

/** The editor's check (RouteTestStep): the test's own start and drops on the country's day, against the window. */
function windowCheck(plan: PlanLike, test: DropGenContainerSummary, runDate?: string | null): RouteTestFact['window'] {
  const strategy = test.ownSections?.gatewayStrategy ? null : (plan.masters?.find((m) => m.id === test.masterId)?.strategy ?? null);
  if (strategy !== ROUTE_TEST_STRATEGY) return null;
  const slots = test.slots.map((s, orderIndex) => ({ enabled: s.enabled, orderIndex, pinnedAtMin: s.pinnedAtMin }));
  const clock = routeTestClockOnCountry({ startMode: test.startMode, startAtMin: test.startAtMin, slots }, planDay(plan, runDate));
  const problem = routeTestDayProblem([{ ...clock, enabled: true, routeTest: true, strategy }], plan.windowStartMin);
  return problem ? { ok: false, text: 'Must go before the send window' } : { ok: true, text: 'Goes before the send window' };
}
